class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.openedAt = 0;
    this.drawerStateVersion = 0;

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      const quickView = this.querySelector('quick-view');
      const stateVersion = this.drawerStateVersion;
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      setTimeout(() => {
        if (stateVersion !== this.drawerStateVersion || this.mainDetailsToggle.hasAttribute('open')) return;
        quickView?.reset();
      }, 450);

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }

  openMenuDrawer(summaryElement = false) {
    const quickView = this.querySelector('quick-view');
    const drawerContent = quickView?.querySelector('.quick-view__content');
    if (quickView?.loaded && !drawerContent?.innerHTML.trim()) quickView.reset();

    this.openedAt = performance.now();
    this.drawerStateVersion += 1;
    super.openMenuDrawer(summaryElement);
  }

  onBodyClick(event) {
    if (performance.now() - this.openedAt < 700) return;
    super.onBodyClick(event);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    const stateVersion = ++this.drawerStateVersion;
    super.closeMenuDrawer(event, elementToFocus);

    setTimeout(() => {
      if (stateVersion !== this.drawerStateVersion || this.mainDetailsToggle.hasAttribute('open')) return;

      document.body.classList.remove(this.classes.open, this.classes.opening, this.classes.closing);
    }, 450);
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const wrapper = this.closest('.card-wrapper');
      const drawer = wrapper.querySelector('quick-view-drawer');
      if (drawer) {
        drawer.querySelector('summary').click();
        drawer.querySelector('quick-view')?.load();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      }
      else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  static scriptPromises = new Map();

  constructor() {
    super();
    this.loading = false;
    this.loaded = false;
    this.observer = null;
  }

  connectedCallback() {
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;

    this.load(observer);
  }

  load(observer = this.observer) {
    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);
    if (this.loaded && !drawerContent?.innerHTML.trim()) this.loaded = false;
    if (this.loading || this.loaded) return;

    observer?.unobserve(this);
    this.loading = true;

    const productUrl = this.dataset.productUrl.split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector(selector);
        if (!productElement) {
          this.loading = false;
          this.observer?.observe(this);
          return;
        }
        const dependenciesReady = this.setInnerHTML(drawerContent, productElement.innerHTML);
        dependenciesReady.catch(e => console.error(e));

        this.loaded = true;
        this.loading = false;
        drawerContent.classList.add('hide-cover');

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        document.dispatchEvent(new CustomEvent('quickview:loaded', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      })
      .catch(e => {
        this.loading = false;
        this.observer?.observe(this);
        console.error(e);
      });
  }

  reset() {
    this.loaded = false;
    if (!this.loading) this.observer?.observe(this);
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;
    const scriptPromises = [];

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      if (oldScriptTag.src) {
        oldScriptTag.remove();
        scriptPromises.push(this.loadExternalScript(oldScriptTag));
        return;
      }

      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });

    return Promise.all(scriptPromises);
  }

  loadExternalScript(sourceScript) {
    const src = sourceScript.src;
    if (QuickView.scriptPromises.has(src)) return QuickView.scriptPromises.get(src);

    const existingScript = Array.from(document.scripts).find(script => script.src === src);
    if (existingScript) return Promise.resolve();

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      Array.from(sourceScript.attributes).forEach(attribute => {
        if (attribute.name !== 'defer') script.setAttribute(attribute.name, attribute.value);
      });
      script.async = false;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load quick view dependency: ${src}`));
      document.head.appendChild(script);
    }).catch(error => {
      QuickView.scriptPromises.delete(src);
      throw error;
    });

    QuickView.scriptPromises.set(src, promise);
    return promise;
  }
}
customElements.define('quick-view', QuickView);
